import { useCallback, useMemo } from 'react';
import { CommonActions } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { 
  RESIDENT_PATH_TO_SCREEN,
  IResidentUserState,
} from '@/constants/residents/residentNavigation';

// Use generic navigation type to support both RootStackParamList and ResidentMobileParamList
type AnyNavigation = {
  navigate: (screen: string, params?: any) => void;
  dispatch: (action: any) => void;
  goBack?: () => void;
};

// Screen metadata type
interface ResidentScreenMetadata {
  title: string;
  path: string;
}

// Screen metadata lookup
const RESIDENT_SCREEN_METADATA: Record<string, ResidentScreenMetadata> = {
  'Payments': { title: 'Payments', path: '/payments' },
  'ResidentPayments': { title: 'Payments', path: '/payments' },
  'LinkedAccounts': { title: 'Linked accounts', path: '/linked-accounts' },
  'ResidentLinkedAccounts': { title: 'Linked accounts', path: '/linked-accounts' },
  'Maintenance': { title: 'Maintenance', path: '/maintenance' },
  'ResidentMaintenance': { title: 'Maintenance', path: '/maintenance' },
  'Events': { title: 'Events', path: '/events' },
  'ResidentEvents': { title: 'Events', path: '/events' },
  'Marketplace': { title: 'Community marketplace', path: '/marketplace' },
  'ResidentMarketplace': { title: 'Community marketplace', path: '/marketplace' },
  'Resources': { title: 'My resources', path: '/resources' },
  'ResidentResources': { title: 'My resources', path: '/resources' },
  'WifiConnection': { title: 'CBX Wi-Fi', path: '/wifi' },
  'ResidentFaq': { title: 'FAQ', path: '/faq' },
  'Messages': { title: 'Property chat', path: '/property-chat' },
  'ResidentMessages': { title: 'Property chat', path: '/property-chat' },
  'BulletinsList': { title: 'Notifications', path: '/notifications' },
  'ResidentNotifications': { title: 'All notifications', path: '/notifications' },
  'Profile': { title: 'My account', path: '/my-account' },
  'ResidentProfile': { title: 'My account', path: '/my-account' },
  'ResidentAccountSettings': { title: 'Account', path: '/account-settings' },
  'CommunityBoard': { title: 'Newsfeed', path: '/newsfeed' },
  'ResidentCommunityBoard': { title: 'Newsfeed', path: '/newsfeed' },
  'Home': { title: 'Home', path: '/home' },
  'ResidentHome': { title: 'Home', path: '/home' },
};

/**
 * Get screen metadata by screen name
 */
export const getResidentScreenMetadata = (screenName: string): ResidentScreenMetadata => {
  return RESIDENT_SCREEN_METADATA[screenName] || { title: screenName, path: '/' };
};

/**
 * Create navigation handler for resident screens (new Figma-based navigation)
 */
export const createResidentNavigationHandler = (
  navigation?: AnyNavigation
) => {
  return (path: string) => {
    if (!navigation) return;
    
    const screenName = RESIDENT_PATH_TO_SCREEN[path];
    if (!screenName) {
      console.warn(`No screen mapping found for path: ${path}`);
      return;
    }
    
    // Handle navigation to new Figma-based screens
    switch (screenName) {
      case 'ResidentHome':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentHome',
            },
          })
        );
        break;
      case 'ResidentPayments':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentPayments',
            },
          })
        );
        break;
      case 'ResidentLinkedAccounts':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentLinkedAccounts',
            },
          })
        );
        break;
      case 'ResidentMaintenance':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentMaintenance',
            },
          })
        );
        break;
      case 'ResidentEvents':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentEvents',
            },
          })
        );
        break;
      case 'ResidentMarketplace':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentMarketplace',
            },
          })
        );
        break;
      case 'ResidentCommunityBoard':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentCommunityBoard',
            },
          })
        );
        break;
      case 'ResidentResources':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentResources',
            },
          })
        );
        break;
      case 'ResidentMessages':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentMessages',
            },
          })
        );
        break;
      case 'ResidentNotifications':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentNotifications',
            },
          })
        );
        break;
      case 'ResidentProfile':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentProfile',
            },
          })
        );
        break;
      case 'ResidentAccountSettings':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentAccountSettings',
            },
          })
        );
        break;
      case 'WifiConnection':
        navigation.navigate('WifiConnection' as any);
        break;
      case 'ResidentFaq':
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Main',
            params: {
              screen: 'ResidentFaq',
            },
          })
        );
        break;
      default:
        console.warn(`Unhandled screen navigation: ${screenName}`);
    }
  };
};

/**
 * Custom hook for resident screen logic
 * Provides reusable logic for resident screens including navigation and sign out
 */
export const useResidentScreen = (
  currentScreenName?: string,
  navigation?: AnyNavigation
) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Build user state for navigation conditions
  const userState: IResidentUserState = useMemo(() => ({
    customerType: user?.customerType,
    isWifiEnabled: (() => {
      try {
        const flags = (user as any)?.property?.featureFlags?.flags || {};
        if (typeof flags.wifiEnabled === 'boolean') return !!flags.wifiEnabled;
        if (typeof flags['cbx-wifi-integration-enabled'] === 'boolean') return !!flags['cbx-wifi-integration-enabled'];
        return false;
      } catch {
        return false;
      }
    })(),
  }), [user]);

  // Get screen metadata
  const screenMetadata = useMemo(
    () => getResidentScreenMetadata(currentScreenName || 'Payments'),
    [currentScreenName]
  );

  // Memoized navigation handler
  const handleNavigate = useMemo(
    () => createResidentNavigationHandler(navigation),
    [navigation]
  );

  // Default logo press handler - navigate to home (default screen)
  const handleLogoPress = useCallback(() => {
    if (!navigation) return;
    navigation.dispatch(
      CommonActions.navigate({
        name: 'Main',
        params: {
          screen: 'ResidentHome',
        },
      })
    );
  }, [navigation]);

  // Sign out handler with proper error handling
  const handleSignOut = useCallback(async () => {
    try {
      await dispatch(logout()).unwrap();
      // Navigation will be handled automatically by RootNavigator
      // when isAuthenticated becomes false
    } catch (error: any) {
      console.error('🚨 Resident sign out failed:', error);
      // Even if logout fails, RootNavigator will handle navigation based on auth state
    }
  }, [dispatch]);

  // Notification press handler - navigate to new notifications screen
  const handleNotificationPress = useCallback(() => {
    if (!navigation) return;
    navigation.dispatch(
      CommonActions.navigate({
        name: 'Main',
        params: {
          screen: 'ResidentNotifications',
        },
      })
    );
  }, [navigation]);

  return {
    userState,
    screenMetadata,
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  };
};

